import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { closeOnlyByButtons } from './dialogGuards';
import { Icon } from './Icon';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** 스크린리더용 다이얼로그 이름 (예: '처방 등록 상세') */
  label: string;
  /** 머리 제목 (Heading 20/B) */
  title: ReactNode;
  /** 제목 아래 보조 문구 (Body 14/R · tertiary) */
  description?: ReactNode;
  /** 하단 고정 영역 (취소/적용 버튼 등) */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Figma Drawer — 오른쪽 540px 패널 + 40% 딤.
 * × · 취소 버튼으로만 닫힌다(딤 클릭 · ESC 로는 닫히지 않는다).
 */
export function Drawer({
  open,
  onClose,
  label,
  title,
  description,
  footer,
  children,
}: DrawerProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-dim" />
        <DialogPrimitive.Content
          {...closeOnlyByButtons}
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 flex w-[540px] max-w-full flex-col bg-surface-white outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {label}
          </DialogPrimitive.Title>
          <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-4 sm:p-6">
            <div className="flex w-full items-start justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1">
                <h2 className="text-heading-20 font-bold text-fg-primary">
                  {title}
                </h2>
                {description && (
                  <p className="text-body-14 font-regular text-fg-tertiary">
                    {description}
                  </p>
                )}
              </div>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="icon-sm" aria-label="닫기">
                  <Icon name="close" size={16} />
                </Button>
              </DialogPrimitive.Close>
            </div>
            {children}
          </div>
          {footer && (
            <div className="flex gap-3.5 px-4 pb-4 sm:px-6 sm:pb-6">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface DrawerSectionProps {
  title: string;
  /** 제목 옆 보조 문구 (Body 13/R · tertiary) — '최신순', '2건' */
  caption?: ReactNode;
  children: ReactNode;
}

/** 드로어 안의 소제목(Subtitle 16/B) 섹션 */
export function DrawerSection({
  title,
  caption,
  children,
}: DrawerSectionProps) {
  return (
    <section className="flex w-full flex-col gap-3">
      <div className="flex items-center gap-2 whitespace-nowrap">
        <h3 className="text-subtitle-16 font-bold text-fg-primary">{title}</h3>
        {caption != null && (
          <span className="text-body-13 font-regular text-fg-tertiary">
            {caption}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
